import React from 'react';
import useAnalytics from 'helpers/useAnalytics';
import _get from 'lodash/get';
import _differenceBy from 'lodash/differenceBy';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import brandColors from 'styles/brandColors';
import { transparentize } from 'polished';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from 'styles/ThemeTyping';
import { useDispatch } from 'react-redux';
import { useData, useStatus } from 'resift';
import { useHistory, Prompt, useLocation } from 'react-router-dom';
import showSnackbar from 'helpers/showSnackbar';
import makeGetUserFieldsFetch from 'fetches/makeGetUserFieldsFetch';
import makeUpdatePermissionScopedEntities from 'fetches/rolesAndPermissions/makeUpdatePermissionScopedEntities';
import makeUpdateRolePermission from 'fetches/rolesAndPermissions/makeUpdateRolePermission';
import makeGetRole from 'fetches/rolesAndPermissions/makeGetRole';
import buildFilterFromAPI from 'pages/RolesAndPermissions/helpers/buildFilterFromAPI';
import transformToApiFilter from 'pages/RolesAndPermissions/helpers/toApiFilter';
import { HOME_URL, ROLES_AND_PERMISSIONS_URL } from 'components/Router/consts';
import { MATCH_ALL_FILTER, PERMISSION_ITEMS } from 'pages/RolesAndPermissions/consts';
import * as Types from 'pages/RolesAndPermissions/typings';
import * as Sift from '@sift/types';

import { Formik, Form } from 'formik';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Paper from '@material-ui/core/Paper';
import CancelButton from 'components/Page/CancelButton';
import Page from 'components/Page/newComponents';
import PageFormToolbar from 'components/Page/newComponents/PageFormToolbar';
import Filters from 'pages/RolesAndPermissions/molecules/Filters';
import EditDisabledNotice from 'pages/RolesAndPermissions/molecules/EditDisabledNotice';
import ManagePermissionScopedEntities from './ManagePermissionScopedEntities';

interface Props {
  role: Types.Role;
  permissionId: string;
  permissionScopedEntitiesResponse: Types.PermissionScopedEntitiesResponse;
}

function ManagePermissionPage({ role, permissionId, permissionScopedEntitiesResponse }: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const fromEditUserId = _get(location, ['state', 'fromEditUserId']);
  const roleId = _get(role, 'id', '');
  const getRole = makeGetRole(roleId);
  const existingRolePermission = _get(role, '_permissions', []).find(
    (rp: Types.PermissionResponse) => rp.permissionId === permissionId,
  );
  const scopeFilterFromAPI = existingRolePermission ? existingRolePermission.scopeFilter : null;
  const apiFilterIsEveryone = _isEqual(scopeFilterFromAPI, MATCH_ALL_FILTER);

  const getUserFields = makeGetUserFieldsFetch();
  const userFieldsData = useData(getUserFields);
  const userFields: Sift.Field[] = _get(userFieldsData, 'results', []);
  const scopeFilter = buildFilterFromAPI(scopeFilterFromAPI, userFields);
  const updatePermissionScopedEntities = makeUpdatePermissionScopedEntities(roleId, permissionId);
  const updateRolePermission = makeUpdateRolePermission(roleId, permissionId);
  const updatePermissionScopedEntitiesStatus = useStatus(updatePermissionScopedEntities);
  const updateFiltersStatus = useStatus(updateRolePermission);

  showSnackbar({
    status: updatePermissionScopedEntitiesStatus,
    message: 'Successfully saved permission audience edits.',
    variant: 'success',
    errorMessage: 'An error occurred saving permission audience edits',
  });

  showSnackbar({
    status: updateFiltersStatus,
    message: 'Successfully saved filter edits.',
    variant: 'success',
    errorMessage: 'An error occurred saving filter edits',
  });

  const disabled = !_get(role, 'permissionsEditable', false);
  const permission = PERMISSION_ITEMS.filter(perm => perm.id === permissionId);
  const permissionTagText = !!permission.length && permission[0].name;

  const handleBack = () => {
    history.push(`${HOME_URL}${ROLES_AND_PERMISSIONS_URL}/roles/${roleId}`, {
      fromEditUserId,
    });
  };

  const initialValues = {
    outerOperator: (scopeFilter.operator || 'and') as Types.Operator,
    everyoneSelected: apiFilterIsEveryone,
    filterGroups: _get(scopeFilter, 'filters', []),
    permissionScopedEntities: _get(permissionScopedEntitiesResponse, 'results', []),
  };

  return (
    <Dialog open fullScreen aria-labelledby="manage-permission">
      <AppBar className={classes.appBar}>
        <Toolbar>
          <Typography id="manage-permission" variant="h6" className={classes.flex}>
            Manage Permission
          </Typography>
          <Button color="inherit" className={classes.cancelButton} onClick={handleBack}>
            Close
          </Button>
        </Toolbar>
      </AppBar>
      <Page classes={{ root: classes.pageRoot }}>
        {disabled && (
          <EditDisabledNotice text="Permissions cannot be edited for this default role. You can still view permission information below." />
        )}

        <Formik initialValues={initialValues} onSubmit={() => {}} validate={() => {}}>
          {props => {
            const { dirty, values, setFieldValue, resetForm } = props;
            const {
              filterGroups,
              outerOperator,
              everyoneSelected,
              permissionScopedEntities,
            } = values;

            const handleSetOuterOperator = (value: Types.Operator) => {
              setFieldValue('outerOperator', value);
            };
            const handleSetFilterGroups = (value: Array<any>) => {
              setFieldValue('filterGroups', value);
            };
            const handleSetEveryoneSelected = (value: boolean) => {
              analytics.track('Roles/Permissions - Create Role - Add Everyone Clicked', {
                category: 'AD - Roles/Permissions',
              });

              setFieldValue('everyoneSelected', value);
            };
            const handleSetPermissionScopedEntities = (value: Array<any>) => {
              setFieldValue('permissionScopedEntities', value);
            };

            const handleSaveManuallyManagedMembers = async () => {
              const _destroyActions = _differenceBy(
                initialValues.permissionScopedEntities,
                permissionScopedEntities,
                'entityId',
              );

              const destroyActions = !_destroyActions.length
                ? []
                : _destroyActions.map(item => ({
                    type: 'destroy' as 'create' | 'destroy',
                    id: item.entityId,
                  }));

              const _createActions = _differenceBy(
                permissionScopedEntities,
                initialValues.permissionScopedEntities,
                'entityId',
              );

              const createActions = !_createActions.length
                ? []
                : _createActions.map(item => ({
                    type: 'create' as 'create' | 'destroy',
                    id: item.entityId,
                    data: { exclude: item.exclude },
                  }));

              const allActions = [...destroyActions, ...createActions];
              !_isEmpty(allActions) && (await dispatch(updatePermissionScopedEntities(allActions)));
            };

            const handleSaveFilters = async () => {
              if (
                _isEqual(everyoneSelected, initialValues.everyoneSelected) &&
                _isEqual(outerOperator, initialValues.outerOperator) &&
                _isEqual(filterGroups, initialValues.filterGroups)
              ) {
                return;
              }

              if (everyoneSelected) {
                await dispatch(
                  updateRolePermission({
                    scopeFilter: MATCH_ALL_FILTER,
                  }),
                );
              } else {
                const scopeFilter = transformToApiFilter(filterGroups, outerOperator);

                await dispatch(updateRolePermission({ scopeFilter }));
              }
            };

            const handleSave = async () => {
              await handleSaveManuallyManagedMembers();
              await handleSaveFilters();
              analytics.track('Roles/Permissions - Create Role - Manage Permissions Saved', {
                category: 'AD - Roles/Permissions',
              });

              await dispatch(getRole());
              handleBack();
            };

            return (
              <Form>
                <Paper className={classes.paper} elevation={2}>
                  <div className={classes.section}>
                    <div className={classes.header}>
                      <DialogTitle className={classes.title}>Permission Scope</DialogTitle>
                      {permissionTagText && (
                        <div className={classes.permissionTag}>{permissionTagText}</div>
                      )}
                    </div>
                    <Typography gutterBottom className={classes.subtitle}>
                      Use the sections below to assign this permission’s scope, either explicitly or
                      dynamically via a filter. Permission scope represents the set of people in
                      which this permission applies to. For example, if the permission is “Edit
                      Profile” and the scope is a filter of Department=”Technology”, then users who
                      have this role will be granted permission to edit profiles in the Technology
                      Department.
                    </Typography>
                  </div>

                  <Divider className={classes.divider} />

                  <div className={classes.section}>
                    <Box>
                      <Typography className={classes.sectionTitle}>
                        Assign Dynamic Filters
                      </Typography>
                      <Typography gutterBottom className={classes.subtitle}>
                        Create a filter to automatically include people as members of the role if
                        they meet the filter conditions. This allows you to automatically
                        include/exclude members as they are onboarded or as their information
                        changes.
                      </Typography>
                    </Box>
                    <Filters
                      filterGroups={filterGroups}
                      shouldDisableAddFilter={everyoneSelected}
                      outerOperator={outerOperator}
                      onSetOuterOperator={handleSetOuterOperator}
                      everyoneSelected={everyoneSelected}
                      onSetFilterGroups={handleSetFilterGroups}
                      onSetEveryoneSelected={handleSetEveryoneSelected}
                      role={role}
                      pageType="permissions"
                    />
                  </div>

                  <div className={classes.section}>
                    <ManagePermissionScopedEntities
                      permissionScopedEntities={permissionScopedEntities}
                      onUpdate={handleSetPermissionScopedEntities}
                      disabled={disabled}
                    />
                  </div>
                </Paper>
                <PageFormToolbar dirty={dirty} rightSide>
                  <CancelButton onClick={resetForm} />
                  <Button
                    onClick={handleSave}
                    disabled={!dirty}
                    variant="contained"
                    color="secondary"
                  >
                    Save and Next
                  </Button>
                </PageFormToolbar>
                <Prompt
                  when={dirty}
                  message={
                    'Your work is not saved, or you have errors that need to be addressed! Are you sure you want to leave?'
                  }
                />
              </Form>
            );
          }}
        </Formik>
      </Page>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  pageRoot: {
    display: 'flex',
    flex: '1 1 auto',
    height: 'calc(100vh - 64px)',
  },
  paper: {
    flexGrow: 1,
    maxWidth: 900,
    marginRight: theme.spacing(3),
    width: '100%',
    paddingBottom: theme.spacing(4),
  },
  section: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  appBar: {
    position: 'relative',
    backgroundColor: '#2f2f32',
  },
  flex: {
    flex: 1,
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(2),
  },
  title: {
    padding: 0,
  },
  subtitle: {
    ...theme.typography.body2,
    color: theme.colors.gray1,
  },
  subtitleMargin: {
    marginLeft: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: theme.typography.fontWeight.semiBold,
  },
  permissionTag: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(3),
    borderRadius: theme.spacing(0.5),
    backgroundColor: transparentize(0.95, theme.palette.common.black),
    color: brandColors.grayScale.gray1,
    padding: theme.spacing(0.5),
    paddingLeft: theme.spacing(),
    paddingRight: theme.spacing(),
  },
  divider: {
    margin: 0,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export default ManagePermissionPage;
